import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { ScheduledBatchSummary } from '@/utils/dashboard';

/**
 * Surfaces the bulk batches queued with a scheduled release so timing is
 * visible from the dashboard, not only inside the bulk-payments flow.
 * Renders nothing when no batch is scheduled.
 */
export default function ScheduledPayoutStrip({ batches }: { batches: ScheduledBatchSummary[] }) {
  const navigate = useNavigate();
  const next = batches.at(0);
  if (!next) return null;

  const alsoQueued = batches.length - 1;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 px-5 py-4 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700">
          <i className="ri-calendar-schedule-line" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground-950">
            Next scheduled payout · {next.reference}
          </p>
          <p className="mt-0.5 text-xs text-foreground-500">
            {next.recipientCount} recipient{next.recipientCount === 1 ? '' : 's'} ·{' '}
            {formatMoney(next.total, next.currency)} · goes out {next.scheduledFor}
            {alsoQueued > 0 ? ` · +${alsoQueued} more queued` : ''}
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        icon="ri-stack-line"
        className="shrink-0"
        onClick={() => navigate('/payments/bulk')}
      >
        Manage schedule
      </Button>
    </section>
  );
}